import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { createJournalEntry, getJournalEntries, deleteJournalEntry, getWeeklyJournalSummary, recordPageVisit } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { useSpeechToText, VoiceInputIndicator } from "@/components/SpeechToText";
import { 
  CalendarIcon, 
  Plus, 
  Save, 
  Trash2, 
  Smile, 
  Meh, 
  Frown, 
  Heart,
  Activity,
  Pill,
  FileText,
  TrendingUp,
  Loader2,
  Mic,
  MicOff,
  Volume2,
  AlertCircle
} from "lucide-react";

// Mood options with translation keys
const getMoodOptions = (t) => [
  { value: 1, label: t("journal.mood.veryLow", "Very Low"), icon: Frown, color: "text-red-500" },
  { value: 2, label: t("journal.mood.low", "Low"), icon: Frown, color: "text-orange-500" },
  { value: 3, label: t("journal.mood.neutral", "Neutral"), icon: Meh, color: "text-yellow-500" },
  { value: 4, label: t("journal.mood.good", "Good"), icon: Smile, color: "text-lime-500" },
  { value: 5, label: t("journal.mood.veryGood", "Very Good"), icon: Smile, color: "text-green-500" }
];

// Symptom options with translation keys
const getSymptomOptions = (t) => [
  { key: "pain", label: t("symptom.pain", "Pain") },
  { key: "breathing", label: t("symptom.breathlessness", "Breathing Difficulties") },
  { key: "nausea", label: t("symptom.nausea", "Nausea") },
  { key: "restlessness", label: t("symptom.restlessness", "Restlessness") },
  { key: "appetite", label: t("symptom.appetiteLoss", "Appetite Changes") },
  { key: "skin", label: t("symptom.skinChanges", "Skin Changes") },
  { key: "anxiety", label: t("symptom.anxiety", "Anxiety") },
  { key: "fatigue", label: t("symptom.fatigue", "Fatigue") },
  { key: "confusion", label: t("symptom.confusion", "Confusion") }
];

export default function CaregiverJournal() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const MOOD_OPTIONS = getMoodOptions(t);
  const SYMPTOM_OPTIONS = getSymptomOptions(t);
  const [date, setDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [weekSummary, setWeekSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeVoiceField, setActiveVoiceField] = useState(null);
  
  // ULTRA-RESILIENT Speech-to-text for observations field - NEVER ABORTS
  const observationsSpeech = useSpeechToText({
    neverAbort: true,
    silentErrors: true,
    maxSilenceMs: 60000, // 60 seconds of silence before auto-restart
    onTranscript: (text) => {
      if (activeVoiceField === 'observations') {
        setFormData(prev => ({
          ...prev,
          observations: prev.observations + text + ' '
        }));
      }
    },
    onError: (message, errorType) => {
      // Only show error for permission denied - silently handle others
      if (errorType === 'not-allowed' || errorType === 'permission-denied') {
        toast.error(message);
        setActiveVoiceField(null);
      }
    }
  });

  // ULTRA-RESILIENT Speech-to-text for notes field - NEVER ABORTS
  const notesSpeech = useSpeechToText({
    neverAbort: true,
    silentErrors: true,
    maxSilenceMs: 60000, // 60 seconds of silence before auto-restart
    onTranscript: (text) => {
      if (activeVoiceField === 'notes') {
        setFormData(prev => ({
          ...prev,
          notes: prev.notes + text + ' '
        }));
      }
    },
    onError: (message, errorType) => {
      // Only show error for permission denied - silently handle others
      if (errorType === 'not-allowed' || errorType === 'permission-denied') {
        toast.error(message);
        setActiveVoiceField(null);
      }
    }
  });
  
  const [formData, setFormData] = useState({
    observations: "",
    mood: 3,
    mood_label: "Neutral",
    symptoms: [],
    symptom_severity: {},
    medications: [],
    notes: ""
  });
  
  const [newMed, setNewMed] = useState({ name: "", dose: "", time: "", given: true });

  // Handle voice input toggle
  const handleVoiceInput = (fieldName) => {
    if (fieldName === 'observations') {
      if (observationsSpeech.isListening) {
        observationsSpeech.stopListening();
        setActiveVoiceField(null);
      } else {
        // Stop notes if listening
        if (notesSpeech.isListening) {
          notesSpeech.stopListening();
        }
        setActiveVoiceField('observations');
        observationsSpeech.startListening();
      }
    } else if (fieldName === 'notes') {
      if (notesSpeech.isListening) {
        notesSpeech.stopListening();
        setActiveVoiceField(null);
      } else {
        // Stop observations if listening
        if (observationsSpeech.isListening) {
          observationsSpeech.stopListening();
        }
        setActiveVoiceField('notes');
        notesSpeech.startListening();
      }
    }
  };

  useEffect(() => {
    recordPageVisit("journal").catch(console.error);
    if (user) {
      loadEntries();
      loadWeekSummary();
    }
    
    // Cleanup on unmount
    return () => {
      if (observationsSpeech.isListening) observationsSpeech.stopListening();
      if (notesSpeech.isListening) notesSpeech.stopListening();
    };
  }, [user]);

  useEffect(() => {
    // Load entry for selected date
    const dateStr = format(date, "yyyy-MM-dd");
    const existingEntry = entries.find(e => e.date === dateStr);
    if (existingEntry) {
      setFormData({
        observations: existingEntry.observations || "",
        mood: existingEntry.mood || 3,
        mood_label: existingEntry.mood_label || "Neutral",
        symptoms: existingEntry.symptoms || [],
        symptom_severity: existingEntry.symptom_severity || {},
        medications: existingEntry.medications || [],
        notes: existingEntry.notes || ""
      });
    } else {
      setFormData({
        observations: "",
        mood: 3,
        mood_label: "Neutral",
        symptoms: [],
        symptom_severity: {},
        medications: [],
        notes: ""
      });
    }
  }, [date, entries]);

  const loadEntries = async () => {
    setIsLoading(true);
    try {
      const data = await getJournalEntries();
      setEntries(data.entries);
    } catch (error) {
      console.error("Error loading entries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadWeekSummary = async () => {
    try {
      const data = await getWeeklyJournalSummary();
      setWeekSummary(data);
    } catch (error) {
      console.error("Error loading summary:", error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await createJournalEntry({
        date: format(date, "yyyy-MM-dd"),
        ...formData
      });
      toast.success("Journal entry saved");
      loadEntries();
      loadWeekSummary();
    } catch (error) {
      toast.error("Failed to save entry");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMoodChange = (value) => {
    const moodOption = MOOD_OPTIONS.find(m => m.value === value);
    setFormData({
      ...formData,
      mood: value,
      mood_label: moodOption?.label || "Neutral"
    });
  };

  const handleSymptomToggle = (symptom) => {
    const newSymptoms = formData.symptoms.includes(symptom)
      ? formData.symptoms.filter(s => s !== symptom)
      : [...formData.symptoms, symptom];
    
    const newSeverity = { ...formData.symptom_severity };
    if (!newSymptoms.includes(symptom)) {
      delete newSeverity[symptom];
    } else if (!newSeverity[symptom]) {
      newSeverity[symptom] = 3;
    }
    
    setFormData({
      ...formData,
      symptoms: newSymptoms,
      symptom_severity: newSeverity
    });
  };

  const handleSeverityChange = (symptom, value) => {
    setFormData({
      ...formData,
      symptom_severity: {
        ...formData.symptom_severity,
        [symptom]: value[0]
      }
    });
  };

  const addMedication = () => {
    if (newMed.name) {
      setFormData({
        ...formData,
        medications: [...formData.medications, { ...newMed }]
      });
      setNewMed({ name: "", dose: "", time: "", given: true });
    }
  };

  const removeMedication = (index) => {
    setFormData({
      ...formData,
      medications: formData.medications.filter((_, i) => i !== index)
    });
  };

  const getMoodIcon = (moodValue) => {
    const option = MOOD_OPTIONS.find(m => m.value === moodValue);
    if (!option) return Meh;
    return option.icon;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center shadow-soft">
          <CardContent className="p-8">
            <Heart className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-2">{t("journal.title")}</h2>
            <p className="text-muted-foreground mb-4">
              {t("journal.signInRequired")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="journal-page">
      {/* Header */}
      <section className="bg-white border-b border-border/50 py-8">
        <div className="container-max px-6 md:px-12">
          <p className="caption mb-2">{t("journal.personalTracking")}</p>
          <h1 className="heading-2 text-foreground mb-2">{t("journal.title")}</h1>
          <p className="text-muted-foreground">
            {t("journal.subtitle")}
          </p>
        </div>
      </section>

      <section className="py-8 px-6 md:px-12">
        <div className="container-max">
          <Tabs defaultValue="entry" className="space-y-6">
            <TabsList className="bg-white shadow-soft">
              <TabsTrigger value="entry" data-testid="tab-entry">
                <FileText className="w-4 h-4 mr-2" />
                {t("journal.dailyEntry")}
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {t("journal.history")}
              </TabsTrigger>
              <TabsTrigger value="summary" data-testid="tab-summary">
                <TrendingUp className="w-4 h-4 mr-2" />
                {t("journal.weeklySummary")}
              </TabsTrigger>
            </TabsList>

            {/* Daily Entry Tab */}
            <TabsContent value="entry">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Date Picker */}
                <Card className="shadow-soft lg:col-span-1" data-testid="date-picker-card">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-primary" />
                      {t("journal.selectDate")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => d && setDate(d)}
                      className="rounded-md border"
                      data-testid="journal-calendar"
                    />
                  </CardContent>
                </Card>

                {/* Entry Form */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Mood */}
                  <Card className="shadow-soft" data-testid="mood-card">
                    <CardHeader>
                      <CardTitle className="font-serif text-lg flex items-center gap-2">
                        <Smile className="w-5 h-5 text-primary" />
                        {t("journal.patientMood")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex justify-between items-center gap-4">
                        {MOOD_OPTIONS.map((option) => {
                          const Icon = option.icon;
                          return (
                            <button
                              key={option.value}
                              onClick={() => handleMoodChange(option.value)}
                              data-testid={`mood-btn-${option.value}`}
                              className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${
                                formData.mood === option.value
                                  ? "bg-primary/10 ring-2 ring-primary"
                                  : "hover:bg-muted"
                              }`}
                            >
                              <Icon className={`w-8 h-8 ${option.color}`} />
                              <span className="text-xs font-medium">{option.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Observations */}
                  <Card className="shadow-soft" data-testid="observations-card">
                    <CardHeader>
                      <CardTitle className="font-serif text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        {t("journal.dailyObservations")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="relative">
                        <Textarea
                          placeholder={t("journal.observationsPlaceholder")}
                          value={formData.observations}
                          onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                          rows={4}
                          className="resize-none pr-12"
                          data-testid="observations-textarea"
                        />
                        {observationsSpeech.isSupported ? (
                          <Button
                            type="button"
                            variant={observationsSpeech.isListening ? "default" : "outline"}
                            size="icon"
                            className={`absolute right-2 top-2 rounded-full transition-all ${
                              observationsSpeech.isListening 
                                ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                                : 'hover:bg-primary/10'
                            }`}
                            onClick={() => handleVoiceInput('observations')}
                            title={observationsSpeech.isListening ? "Stop recording" : "Start voice input"}
                            data-testid="voice-observations-btn"
                          >
                            {observationsSpeech.isListening ? (
                              <MicOff className="w-4 h-4" />
                            ) : (
                              <Mic className="w-4 h-4" />
                            )}
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled
                            className="absolute right-2 top-2 rounded-full opacity-50"
                            title="Voice input not supported in this browser"
                          >
                            <MicOff className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <VoiceInputIndicator 
                        isListening={observationsSpeech.isListening} 
                        interimTranscript={observationsSpeech.interimTranscript}
                      />
                      {observationsSpeech.error && (
                        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {observationsSpeech.error}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Symptoms */}
                  <Card className="shadow-soft" data-testid="symptoms-card">
                    <CardHeader>
                      <CardTitle className="font-serif text-lg flex items-center gap-2">
                        <Activity className="w-5 h-5 text-primary" />
                        {t("journal.symptomsObserved")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex flex-wrap gap-2">
                        {SYMPTOM_OPTIONS.map((symptom) => (
                          <Badge
                            key={symptom.key}
                            variant={formData.symptoms.includes(symptom.key) ? "default" : "outline"}
                            className="cursor-pointer"
                            onClick={() => handleSymptomToggle(symptom.key)}
                            data-testid={`symptom-badge-${symptom.key}`}
                          >
                            {symptom.label}
                          </Badge>
                        ))}
                      </div>
                      
                      {formData.symptoms.length > 0 && (
                        <div className="space-y-4 pt-4 border-t">
                          <p className="text-sm text-muted-foreground">{t("journal.rateSeverity")}</p>
                          {formData.symptoms.map((symptomKey) => {
                            const symptom = SYMPTOM_OPTIONS.find(s => s.key === symptomKey);
                            return (
                            <div key={symptomKey} className="flex items-center gap-4">
                              <span className="w-32 text-sm font-medium">{symptom?.label || symptomKey}</span>
                              <Slider
                                value={[formData.symptom_severity[symptomKey] || 3]}
                                onValueChange={(v) => handleSeverityChange(symptomKey, v)}
                                max={5}
                                min={1}
                                step={1}
                                className="flex-1"
                              />
                              <span className="w-8 text-center font-medium">
                                {formData.symptom_severity[symptomKey] || 3}
                              </span>
                            </div>
                          );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Medications */}
                  <Card className="shadow-soft" data-testid="medications-card">
                    <CardHeader>
                      <CardTitle className="font-serif text-lg flex items-center gap-2">
                        <Pill className="w-5 h-5 text-primary" />
                        {t("journal.medicationLog")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Add medication form */}
                      <div className="flex flex-wrap gap-2 items-end">
                        <div className="flex-1 min-w-[150px]">
                          <Label className="text-xs">{t("journal.medication")}</Label>
                          <Input
                            placeholder={t("auth.name", "Name")}
                            value={newMed.name}
                            onChange={(e) => setNewMed({ ...newMed, name: e.target.value })}
                            data-testid="med-name-input"
                          />
                        </div>
                        <div className="w-24">
                          <Label className="text-xs">{t("journal.dose")}</Label>
                          <Input
                            placeholder="10mg"
                            value={newMed.dose}
                            onChange={(e) => setNewMed({ ...newMed, dose: e.target.value })}
                            data-testid="med-dose-input"
                          />
                        </div>
                        <div className="w-24">
                          <Label className="text-xs">{t("journal.time")}</Label>
                          <Input
                            type="time"
                            value={newMed.time}
                            onChange={(e) => setNewMed({ ...newMed, time: e.target.value })}
                            data-testid="med-time-input"
                          />
                        </div>
                        <Button size="sm" onClick={addMedication} data-testid="add-med-btn">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Medication list */}
                      {formData.medications.length > 0 && (
                        <div className="space-y-2 pt-2">
                          {formData.medications.map((med, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={med.given}
                                  onCheckedChange={(checked) => {
                                    const newMeds = [...formData.medications];
                                    newMeds[index].given = checked;
                                    setFormData({ ...formData, medications: newMeds });
                                  }}
                                />
                                <span className={med.given ? "" : "line-through text-muted-foreground"}>
                                  {med.name} {med.dose && `(${med.dose})`} {med.time && `@ ${med.time}`}
                                </span>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => removeMedication(index)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Notes */}
                  <Card className="shadow-soft">
                    <CardHeader>
                      <CardTitle className="font-serif text-lg">{t("journal.additionalNotes")}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="relative">
                        <Textarea
                          placeholder={t("journal.notesPlaceholder")}
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          rows={3}
                          className="resize-none pr-12"
                          data-testid="notes-textarea"
                        />
                        {notesSpeech.isSupported ? (
                          <Button
                            type="button"
                            variant={notesSpeech.isListening ? "default" : "outline"}
                            size="icon"
                            className={`absolute right-2 top-2 rounded-full transition-all ${
                              notesSpeech.isListening 
                                ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                                : 'hover:bg-primary/10'
                            }`}
                            onClick={() => handleVoiceInput('notes')}
                            title={notesSpeech.isListening ? "Stop recording" : "Start voice input"}
                            data-testid="voice-notes-btn"
                          >
                            {notesSpeech.isListening ? (
                              <MicOff className="w-4 h-4" />
                            ) : (
                              <Mic className="w-4 h-4" />
                            )}
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled
                            className="absolute right-2 top-2 rounded-full opacity-50"
                            title="Voice input not supported in this browser"
                          >
                            <MicOff className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <VoiceInputIndicator 
                        isListening={notesSpeech.isListening} 
                        interimTranscript={notesSpeech.interimTranscript}
                      />
                      {notesSpeech.error && (
                        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {notesSpeech.error}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Save Button */}
                  <Button 
                    onClick={handleSave} 
                    disabled={isSaving}
                    className="w-full btn-primary"
                    data-testid="save-entry-btn"
                  >
                    {isSaving ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Save className="w-5 h-5 mr-2" />
                    )}
                    {t("journal.saveEntry")} {format(date, "MMMM d, yyyy")}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history">
              <Card className="shadow-soft" data-testid="history-card">
                <CardHeader>
                  <CardTitle className="font-serif text-lg">{t("journal.journalHistory")}</CardTitle>
                  <CardDescription>{t("journal.historyDescription")}</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : entries.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">{t("journal.noEntries")}</p>
                  ) : (
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-4">
                        {entries.map((entry) => {
                          const MoodIcon = getMoodIcon(entry.mood);
                          return (
                            <div key={entry.id} className="p-4 border rounded-xl hover:bg-muted/50 transition-colors" data-testid={`entry-${entry.id}`}>
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium">{format(new Date(entry.date), "MMMM d, yyyy")}</span>
                                  <MoodIcon className={`w-5 h-5 ${MOOD_OPTIONS.find(m => m.value === entry.mood)?.color}`} />
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={async () => {
                                    await deleteJournalEntry(entry.id);
                                    loadEntries();
                                    toast.success("Entry deleted");
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                              {entry.observations && (
                                <p className="text-sm text-muted-foreground mb-2">{entry.observations}</p>
                              )}
                              {entry.symptoms?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {entry.symptoms.map((s) => (
                                    <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Summary Tab */}
            <TabsContent value="summary">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="shadow-soft" data-testid="mood-summary-card">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg">{t("journal.moodTrend")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {weekSummary ? (
                      <div className="space-y-4">
                        <div className="text-center">
                          <p className="text-4xl font-serif font-semibold text-primary">
                            {weekSummary.average_mood?.toFixed(1) || "N/A"}
                          </p>
                          <p className="text-muted-foreground">{t("journal.averageMood")}</p>
                        </div>
                        <div className="flex justify-center gap-2">
                          {weekSummary.entries?.map((entry, i) => {
                            const Icon = getMoodIcon(entry.mood);
                            return (
                              <div key={i} className="flex flex-col items-center">
                                <Icon className={`w-6 h-6 ${MOOD_OPTIONS.find(m => m.value === entry.mood)?.color}`} />
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(entry.date), "EEE")}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">{t("journal.noData")}</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="shadow-soft" data-testid="symptom-summary-card">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg">{t("journal.symptomFrequency")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {weekSummary?.symptom_frequency && Object.keys(weekSummary.symptom_frequency).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(weekSummary.symptom_frequency)
                          .sort((a, b) => b[1] - a[1])
                          .map(([symptom, count]) => (
                            <div key={symptom} className="flex items-center justify-between">
                              <span className="text-sm">{symptom}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-primary rounded-full"
                                    style={{ width: `${(count / 7) * 100}%` }}
                                  />
                                </div>
                                <span className="text-sm text-muted-foreground w-8">{count}x</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">{t("journal.noSymptomsLogged")}</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
